import { CharacteristicEnum } from "../../handlers/types";
import WeaponType from "../enums/WeaponType";

/**
 * Defines all unlocks at specific character upgrade levels (tiers)
 */

export const PROGRESSION_TIERS = {
  // Starting tier - available from the beginning
  0: {
    characteristics: ["maxHp", "power"],
    weapons: [WeaponType.HASHBLADE, WeaponType.BOOMERANG],
    powerups: [
      WeaponType.POWER,
      WeaponType.RANGE,
      WeaponType.FORK,
      WeaponType.MOVESPEED,
      WeaponType.MAGNET,
      WeaponType.SPEED,
    ],
    looks: [],
  },

  // Tier 1 - early progression
  1: {
    characteristics: ["moveSpeed"],
    weapons: [WeaponType.BLAST],
    powerups: [WeaponType.DURATION],
    looks: [],
  },

  // Tier 2
  3: {
    characteristics: ["growth"],
    weapons: [],
    powerups: [WeaponType.MAXHEALTH],
    looks: [],
  },

  // Tier 3
  5: {
    characteristics: ["luck", "magnet"],
    weapons: [WeaponType.ROCK],
    powerups: [],
    looks: ["char2"],
  },

  // Tier 4
  10: {
    characteristics: ["speed", "duration"],
    weapons: [],
    powerups: [WeaponType.GROWTH],
    looks: ["char3"],
  },

  // Tier 4.5
  15: {
    characteristics: [],
    weapons: [WeaponType.BUBBLE],
    powerups: [],
    looks: [],
  },

  // Tier 5
  17: {
    characteristics: ["cooldown", "area", "amount"],
    weapons: [],
    powerups: [WeaponType.COOLDOWN],
    looks: [],
  },

  // Tier 6
  26: {
    characteristics: [],
    weapons: [],
    powerups: [WeaponType.ARMOR],
    looks: ["char4"],
  },

  // Tier 7
  37: {
    characteristics: [],
    weapons: [],
    powerups: [WeaponType.LUCK],
    looks: [],
  },

  // Tier 8
  50: {
    characteristics: [],
    weapons: [],
    powerups: [],
    looks: [],
  },
};

/**
 * Helper function to get all unlocked characteristics up to a given level
 */
export function getUnlockedCharacteristics(
  totalUpgradeLevel: number
): string[] {
  let unlocked: string[] = [];

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (totalUpgradeLevel >= tierLevel) {
      unlocked = [...unlocked, ...tierData.characteristics];
    }
  });

  return unlocked;
}

/**
 * Helper function to get all unlocked weapons up to a given level
 */
export function getUnlockedWeapons(totalUpgradeLevel: number): WeaponType[] {
  let unlocked: WeaponType[] = [];

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (totalUpgradeLevel >= tierLevel) {
      unlocked = [...unlocked, ...tierData.weapons];
    }
  });

  return unlocked;
}

/**
 * Helper function to get all unlocked powerups up to a given level
 */
export function getUnlockedPowerups(totalUpgradeLevel: number): WeaponType[] {
  let unlocked: WeaponType[] = [];

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (totalUpgradeLevel >= tierLevel) {
      unlocked = [...unlocked, ...tierData.powerups];
    }
  });

  return unlocked;
}

/**
 * Helper function to get all unlocked looks up to a given level
 */
export function getUnlockedLooks(totalUpgradeLevel: number): string[] {
  let unlocked: string[] = [];

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (totalUpgradeLevel >= tierLevel) {
      unlocked = [...unlocked, ...tierData.looks];
    }
  });

  return unlocked;
}

/**
 * Helper function to check if a specific characteristic is unlocked
 */
export function isCharacteristicUnlocked(
  characteristic: string,
  totalUpgradeLevel: number
): boolean {
  return getUnlockedCharacteristics(totalUpgradeLevel).includes(characteristic);
}

/**
 * Helper function to check if a specific weapon is unlocked
 */
export function isWeaponUnlocked(
  weaponType: WeaponType,
  totalUpgradeLevel: number
): boolean {
  return getUnlockedWeapons(totalUpgradeLevel).includes(weaponType);
}

/**
 * Helper function to check if a specific powerup is unlocked
 */
export function isPowerupUnlocked(
  powerupType: WeaponType,
  totalUpgradeLevel: number
): boolean {
  return getUnlockedPowerups(totalUpgradeLevel).includes(powerupType);
}

/**
 * Helper function to check if a specific look is unlocked
 */
export function isLookUnlocked(
  lookId: string,
  totalUpgradeLevel: number
): boolean {
  return getUnlockedLooks(totalUpgradeLevel).includes(lookId);
}

/**
 * Get the next tier level that will unlock something new
 */
export function getNextUnlockTier(totalUpgradeLevel: number): number | null {
  const tiers = Object.keys(PROGRESSION_TIERS)
    .map((level) => parseInt(level))
    .sort((a, b) => a - b);

  for (const tier of tiers) {
    if (tier > totalUpgradeLevel) {
      return tier;
    }
  }

  return null; // No more tiers
}

/**
 * Get information about what will be unlocked at the next tier
 */
export function getNextUnlockInfo(totalUpgradeLevel: number): {
  tier: number;
  characteristics: string[];
  weapons: WeaponType[];
  powerups: WeaponType[];
  looks: string[];
} | null {
  const nextTier = getNextUnlockTier(totalUpgradeLevel);
  if (nextTier === null) return null;

  const tierData = PROGRESSION_TIERS[nextTier];
  return {
    tier: nextTier,
    characteristics: tierData.characteristics,
    weapons: tierData.weapons,
    powerups: tierData.powerups,
    looks: tierData.looks,
  };
}

/**
 * Get information about what was just unlocked when reaching a new level
 */
export function getNewUnlocks(
  previousLevel: number,
  currentLevel: number
): {
  tiers: number[];
  characteristics: string[];
  weapons: WeaponType[];
  powerups: WeaponType[];
  looks: string[];
} {
  const result = {
    tiers: [] as number[],
    characteristics: [] as string[],
    weapons: [] as WeaponType[],
    powerups: [] as WeaponType[],
    looks: [] as string[],
  };

  // Find all tiers that were crossed
  Object.keys(PROGRESSION_TIERS)
    .map((level) => parseInt(level))
    .sort((a, b) => a - b)
    .forEach((tier) => {
      if (previousLevel < tier && currentLevel >= tier) {
        const tierData = PROGRESSION_TIERS[tier];

        result.tiers.push(tier);
        result.characteristics.push(...tierData.characteristics);
        result.weapons.push(...tierData.weapons);
        result.powerups.push(...tierData.powerups);
        result.looks.push(...tierData.looks);
      }
    });

  return result;
}

/**
 * Get all available tiers and their requirements
 */
export function getAllTiers(): { level: number }[] {
  return Object.entries(PROGRESSION_TIERS)
    .map(([level, data]) => ({
      level: parseInt(level),
    }))
    .sort((a, b) => a.level - b.level);
}

/**
 * Get all characteristics organized by tier
 */
export function getCharacteristicsByTier(): Record<
  number,
  CharacteristicEnum[]
> {
  const characteristicsByTier: Record<number, CharacteristicEnum[]> = {};

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (tierData.characteristics.length > 0) {
      characteristicsByTier[tierLevel] =
        tierData.characteristics as CharacteristicEnum[];
    }
  });

  return characteristicsByTier;
}

/**
 * Get all weapons organized by tier
 */
export function getWeaponsByTier(): Record<number, WeaponType[]> {
  const weaponsByTier: Record<number, WeaponType[]> = {};

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (tierData.weapons.length > 0) {
      weaponsByTier[tierLevel] = tierData.weapons;
    }
  });

  return weaponsByTier;
}

/**
 * Get all powerups organized by tier
 */
export function getPowerupsByTier(): Record<number, WeaponType[]> {
  const powerupsByTier: Record<number, WeaponType[]> = {};

  Object.entries(PROGRESSION_TIERS).forEach(([tierLevelStr, tierData]) => {
    const tierLevel = parseInt(tierLevelStr);
    if (tierData.powerups.length > 0) {
      powerupsByTier[tierLevel] = tierData.powerups;
    }
  });

  return powerupsByTier;
}
